import {
  PositionedModelController,
  PositionedModelOwnerController,
  ResponsiveLayout,
  WindowSize,
  MediaContainerController,
  PromptContainerController,
} from '@sas-dvr/ltjs-bird';
import {
  Dimension,
  DimensionUnit,
  LayoutConstraint,
  LayoutContext,
  LayoutProxy,
} from '@sas-dvr/ltjs-commons';
import {
  LayoutItem,
  LayoutState,
} from '@sas-dvr/internal-va-react-core/store/birdLayout/BirdLayoutState';
import { getKey, getScrollbarState } from './stateUtils';
import { DeepReadonly } from '@sas-dvr/internal-va-react-core/utility-types';
import { getScrollbarSize } from '@sas-dvr/internal-va-react-core/utils/scrollbarSize';
import { getLocaleTextDir } from '@sas-dvr/internal-va-react-core/config';

export const TILE_GAP = 10;
export const CONTAINER_INSET = 5;
export const PADDING = 3;

function getLayoutConstraint(controller: PositionedModelController) {
  const layoutProxy = controller.getLayoutProxy();
  if (!layoutProxy) {
    return null;
  }
  return getOrCreateLayoutConstraint(layoutProxy);
}

export function getOrCreateLayoutConstraint(layoutProxy: LayoutProxy) {
  let layoutConstraint = layoutProxy.getConstraint();
  if (!layoutConstraint) {
    layoutConstraint = new LayoutConstraint();
    layoutProxy.setDefaultConstraint(layoutConstraint);
  }
  return layoutConstraint;
}

function refreshLayoutConstraint(
  layoutConstraint: LayoutConstraint,
  data: DeepReadonly<Partial<LayoutItem>>
) {
  // NOTE: FooterHeight is set on HEIGHT_REQUEST via ./birdRichText's getTitleObservables
  let rightSpace = 0;
  const scrollbars = data.scrollbars;
  if (scrollbars) {
    rightSpace = !!scrollbars.vertical ? getScrollbarSize() : 0;
  }

  const constraints = data.constraints;
  if (constraints) {
    if (constraints.preferredHeight !== undefined && !isNaN(constraints.preferredHeight)) {
      layoutConstraint.setHeight(new Dimension(constraints.preferredHeight, DimensionUnit.PIXELS));
    }
    if (constraints.preferredWidth !== undefined && !isNaN(constraints.preferredWidth)) {
      layoutConstraint.setWidth(new Dimension(constraints.preferredWidth, DimensionUnit.PIXELS));
    }

    layoutConstraint.setMinimumHeight(constraints.minHeight || NaN);
    layoutConstraint.setMinimumWidth(constraints.minWidth || NaN);

    if (
      constraints.additionalContentWidth !== undefined &&
      !isNaN(constraints.additionalContentWidth)
    ) {
      rightSpace += constraints.additionalContentWidth;
    }
  }

  if (getLocaleTextDir() === 'rtl') {
    layoutConstraint.setLeftSpace(rightSpace);
  } else {
    layoutConstraint.setRightSpace(rightSpace);
  }
}

export function refreshLayoutConstraints(
  controller: PositionedModelController,
  state: LayoutState
) {
  const key = getKey(controller);
  if (
    controller instanceof PositionedModelOwnerController &&
    !(controller instanceof PromptContainerController)
  ) {
    const children = controller.getOrderedVisualAndContainerControllers();
    if (!children) {
      return;
    }
    for (const child of children) {
      if (!child) {
        continue;
      }
      refreshLayoutConstraints(child, state);
    }
  }

  const data = state[key];
  const layoutConstraint = getLayoutConstraint(controller);
  if (!layoutConstraint || !data) {
    return;
  }
  refreshLayoutConstraint(layoutConstraint, data);

  if (controller instanceof MediaContainerController) {
    controller.refreshLayoutConstraints();
  }
}

function doBIRDLayoutImpl(controller: PositionedModelOwnerController, state: LayoutState) {
  const reportController = controller.getReportController();
  const model = controller.getModel();
  const layoutProxy = controller.getLayoutProxy();
  if (!reportController || !model || !layoutProxy) {
    return;
  }
  const layout = model.getLayout();
  const layoutContext = new LayoutContext();

  if (layout instanceof ResponsiveLayout) {
    // const currentRange = sap.ui.Device.media.getCurrentRange(sap.ui.Device.media.RANGESETS.SAP_STANDARD);
    // var mediaName = currentRange ? currentRange.name : 'Desktop';
    const windowSize = /*MEDIA_MAP[mediaName] || */ WindowSize.LARGE;
    reportController.setWindowSize(windowSize);
    //Set up layout specific context properties
    layoutContext.setTileGap(0);
    layoutContext.setContainerInset(0);
  } else {
    layoutContext.setTileGap(TILE_GAP);
    layoutContext.setContainerInset(CONTAINER_INSET);
  }

  layoutContext.setPositionTiledContentRelativeToRoot(false);
  layoutContext.setEnableTileReflow(false);
  layoutContext.setEnableTileMinimums(true);
  layoutContext.setDisableRecursion(true);
  // Setting this to false ensures that objects are sized correctly, but the padding and
  // border need to be removed from the position later.
  layoutContext.setIgnoreContainerPadding(false);
  const config = reportController.getSASReportConfiguration();
  if (config) {
    layoutContext.setDefaultMinimumHeight(config.getSectionHeaderHeight());
  }

  if (getLocaleTextDir() === 'rtl') {
    layoutContext.setInvertXSpace(true);
  }

  const key = getKey(controller);
  const layoutData = state[key];
  const layoutConstraint = getLayoutConstraint(controller);
  if (!layoutConstraint || !layoutData) {
    return;
  }
  let scrollState = {
    layoutChanged: true,
    state: layoutData.scrollbars,
  };
  while (scrollState.layoutChanged) {
    refreshLayoutConstraint(layoutConstraint, {
      ...layoutData,
      scrollbars: scrollState.state,
    });
    layoutProxy.layout(layoutContext);
    scrollState = getScrollbarState(controller, scrollState.state);
  }

  const containerChildren = controller.getContainerControllers();
  if (containerChildren) {
    for (const child of containerChildren) {
      if (!child) {
        continue;
      }
      doBIRDLayoutImpl(child, state);
    }
  }
}

export function doBirdLayout(
  containerController: PositionedModelController,
  state: LayoutState,
  width: number | undefined,
  height: number | undefined
) {
  const layoutProxy = containerController.getLayoutProxy();
  if (layoutProxy) {
    layoutProxy.setX(new Dimension(0, DimensionUnit.PIXELS));
    layoutProxy.setY(new Dimension(0, DimensionUnit.PIXELS));
    layoutProxy.setW(new Dimension(width || 0, DimensionUnit.PIXELS));
    layoutProxy.setH(new Dimension(height || 0, DimensionUnit.PIXELS));
  }
  if (containerController instanceof PositionedModelOwnerController) {
    doBIRDLayoutImpl(containerController, state);
  }
}
